import {
  FiEye,
  FiFileText,
  FiArrowRight,
  FiDollarSign,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

export default function RecentInvoices({ invoices = [] }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden">
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div>
          <h2 className="text-xl font-bold">Recent Invoices</h2>

          <p className="text-sm text-gray-500 mt-1">
            Latest generated invoices
          </p>
        </div>

        <button
          onClick={() => navigate("/admindashboard/sales/invoice/list")}
          className="text-primary-700 font-semibold flex items-center gap-2"
        >
          View All
          <FiArrowRight />
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {invoices.length ? (
          invoices.map((invoice) => {
            const total = Number(invoice.totalAmount || 0);
            const paid = Number(invoice.paidAmount || 0);
            const balance = Math.max(total - paid, 0);
            const paymentStatus = getPaymentStatus(
              invoice.paymentStatus,
              balance,
            );

            return (
              <div
                key={invoice.id}
                className="p-5 hover:bg-gray-50 transition"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex gap-4 min-w-0">
                    <div className="h-12 w-12 shrink-0 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center">
                      <FiFileText size={20} />
                    </div>

                    <div className="min-w-0">
                      <h4 className="font-semibold truncate">
                        {invoice.invoiceCode}
                      </h4>

                      <p className="text-sm text-gray-500 mt-1 truncate">
                        {invoice.customerNameSnapshot}
                      </p>

                      <p className="text-xs text-gray-400 mt-1">
                        {formatDateTime(invoice.invoiceDate)}
                      </p>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <h3 className="font-bold text-lg">
                      {formatMoney(total)}
                    </h3>

                    <div className="flex items-center justify-end gap-1 mt-1 text-xs text-gray-500">
                      <FiDollarSign size={12} />
                      Balance {formatMoney(balance)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-5">
                  <Status status={paymentStatus} />

                  <button
                    onClick={() =>
                      navigate(`/admindashboard/sales/invoice/${invoice.id}`)
                    }
                    className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white transition"
                    title="View invoice"
                  >
                    <FiEye />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-16 text-center text-gray-500">
            No invoices found.
          </div>
        )}
      </div>
    </div>
  );
}

function Status({ status }) {
  const styles = {
    Paid: "bg-green-100 text-green-700",
    Partial: "bg-blue-100 text-blue-700",
    Unpaid: "bg-orange-100 text-orange-700",
    Cancelled: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        styles[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function getPaymentStatus(paymentStatus, balance) {
  if (paymentStatus === "PAID" || balance <= 0) return "Paid";
  if (paymentStatus === "PARTIALLY_PAID") return "Partial";
  return "Unpaid";
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  const today = new Date();

  if (date.toDateString() === today.toDateString()) {
    return `Today · ${date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}